// Progressive Overload 4-Week Block.
//
// The base week stored in `profiles.ai_plan.workout_plan` is the immutable
// source. Weeks 1-4 are derived deterministically here on read — no Claude
// regeneration, no DB migration, no risk of drift across weeks.

import { inferMusclesByName, type MuscleId } from "./muscle-map";
import type { PlanExercise, PlanWorkoutDay } from "./plan-ops";

export type BlockWeekNumber = 1 | 2 | 3 | 4;

export interface BlockWeekMeta {
  week: BlockWeekNumber;
  label: string;          // "Base", "Build", "Peak", "Deload"
  phase: string;          // "Green", "Yellow", "Red", "Green"
  tagline: string;        // short explanation
  /** Token-anchored fatigue color used for swatches. Matches `fatigueColor`. */
  color: string;
  rpeCap: number;
}

export const BLOCK_WEEKS: readonly BlockWeekMeta[] = [
  { week: 1, label: "Base",   phase: "Green",  tagline: "Establish base volume · RPE 7",     color: "hsl(140 70% 50%)", rpeCap: 7 },
  { week: 2, label: "Build",  phase: "Yellow", tagline: "+1 set on compounds · RPE 8",       color: "hsl(60 80% 50%)",  rpeCap: 8 },
  { week: 3, label: "Peak",   phase: "Red",    tagline: "Reps +2 · RPE 9 · peak intensity",  color: "hsl(0 75% 55%)",   rpeCap: 9 },
  { week: 4, label: "Deload", phase: "Green",  tagline: "Sets cut 50% · RPE 5 · recover",    color: "hsl(140 70% 50%)", rpeCap: 5 },
] as const;

export function getBlockWeekMeta(week: number): BlockWeekMeta {
  const w = clampWeek(week);
  return BLOCK_WEEKS[w - 1];
}

export function clampWeek(week: unknown): BlockWeekNumber {
  const n = Math.round(Number(week));
  if (n === 2 || n === 3 || n === 4) return n;
  return 1;
}

/** Active block week is stored on the plan; default = 1. */
export function getActiveBlockWeek(aiPlan: unknown): BlockWeekNumber {
  const raw = (aiPlan as { active_block_week?: unknown } | null)?.active_block_week;
  return clampWeek(raw);
}

const ISOLATION_PATTERNS = /(curl|extension|raise|fly|pushdown|kickback|lateral|shrug|crunch|calf|forearm|wrist)/i;
const COMPOUND_KEYWORDS = /(press|row|pull[- ]?up|chin[- ]?up|pulldown|hip thrust|rdl|romanian|squat|lunge|deadlift|clean|snatch|push[- ]?up|dip)/i;

/** Heuristic: is this exercise a primary multi-joint movement? */
export function isCompound(ex: Pick<PlanExercise, "name" | "slug">): boolean {
  const haystack = `${ex.slug ?? ""} ${ex.name ?? ""}`;
  if (ISOLATION_PATTERNS.test(haystack)) return false;
  if (COMPOUND_KEYWORDS.test(haystack)) return true;
  // Fallback: treat as accessory. Conservative for Week 2 set bumps.
  return false;
}

function adjustRpe(baseRpe: PlanExercise["rpe"], cap: number): string | number {
  if (typeof baseRpe === "number") return Math.min(baseRpe, cap);
  if (typeof baseRpe === "string" && baseRpe.trim()) {
    // RPE ranges like "7-9" -> cap each end.
    const m = baseRpe.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
    if (m) {
      const lo = Math.min(Number(m[1]), cap);
      const hi = Math.min(Number(m[2]), cap);
      return lo === hi ? String(lo) : `${lo}-${hi}`;
    }
    const single = Number(baseRpe);
    if (Number.isFinite(single)) return Math.min(single, cap);
    return baseRpe;
  }
  return cap;
}

function shiftRepsLabel(repsLabel: string | undefined, delta: number): string {
  const label = repsLabel ?? "8-12";
  if (delta === 0) return label;
  const range = label.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) {
    const lo = Math.max(1, Number(range[1]) + delta);
    const hi = Math.max(lo, Number(range[2]) + delta);
    return `${lo}-${hi}`;
  }
  const single = Number(label);
  if (Number.isFinite(single)) return String(Math.max(1, single + delta));
  return label;
}

/** Apply Week N transform to a single exercise. Returns a new object. */
export function applyBlockWeek(ex: PlanExercise, week: BlockWeekNumber): PlanExercise {
  const baseSets = Math.max(1, Math.round(Number(ex.sets ?? 3)));
  const meta = getBlockWeekMeta(week);

  let sets = baseSets;
  let repsDelta = 0;

  switch (week) {
    case 1:
      sets = baseSets;
      repsDelta = 0;
      break;
    case 2:
      sets = isCompound(ex) ? baseSets + 1 : baseSets;
      repsDelta = 0;
      break;
    case 3:
      sets = baseSets;
      repsDelta = 2;
      break;
    case 4:
      sets = Math.max(1, Math.round(baseSets * 0.5));
      repsDelta = 0;
      break;
  }

  return {
    ...ex,
    sets,
    repsLabel: shiftRepsLabel(ex.repsLabel, repsDelta),
    rpe: adjustRpe(ex.rpe, meta.rpeCap),
  };
}

/** Apply Week N transform to every exercise on every day of the plan. */
export function applyBlockWeekToPlan<T extends { workout_plan?: PlanWorkoutDay[] }>(
  plan: T | null | undefined,
  week: BlockWeekNumber,
): T | null | undefined {
  if (!plan || !Array.isArray(plan.workout_plan)) return plan;
  return {
    ...plan,
    workout_plan: plan.workout_plan.map((day) => ({
      ...day,
      exercises: (day.exercises ?? []).map((ex) => applyBlockWeek(ex, week)),
    })),
  };
}

/**
 * Total planned sets per muscle for the selected week, using the same
 * primary=1.0 / secondary=0.5 weighting the fatigue model uses for logged sets.
 * Drives the heat-map "planned volume" mode.
 */
export function getPlannedWeeklySetsByMuscle(
  aiPlan: unknown,
  week: BlockWeekNumber,
): Map<MuscleId, number> {
  const totals = new Map<MuscleId, number>();
  const raw = (aiPlan as { workout_plan?: PlanWorkoutDay[] } | null)?.workout_plan;
  if (!Array.isArray(raw)) return totals;

  for (const day of raw) {
    for (const ex of day?.exercises ?? []) {
      const adjusted = applyBlockWeek(ex, week);
      const sets = Math.max(0, Number(adjusted.sets ?? 0));
      if (!sets) continue;
      const { primary, secondary } = inferMusclesByName(ex.slug ?? "", ex.name ?? "");
      for (const m of primary) totals.set(m, (totals.get(m) ?? 0) + sets);
      for (const m of secondary) totals.set(m, (totals.get(m) ?? 0) + sets * 0.5);
    }
  }
  return totals;
}

/**
 * Convert planned set totals to 0-100 scores normalized to the user's own peak
 * muscle volume — same pattern as the live fatigue normalization, so the colors
 * read consistently against the existing `fatigueColor` gradient.
 */
export function plannedSetsToScores(totals: Map<MuscleId, number>): Map<MuscleId, number> {
  const out = new Map<MuscleId, number>();
  let peak = 0;
  totals.forEach((v) => { if (v > peak) peak = v; });
  if (peak <= 0) return out;
  totals.forEach((v, k) => {
    out.set(k, Math.max(0, Math.min(100, Math.round((v / peak) * 100))));
  });
  return out;
}
